import type {
  GenericError,
  ValidationError,
  ValidationErrorItem,
} from '@zelos/codegen/apis';
import {
  ApiError,
} from '@zelos/codegen/apis';
import type {
  UseFormSetError,
  FieldPath,
  FieldValues,
} from 'react-hook-form';

export class TypedApiError extends ApiError {
  declare readonly body: GenericError | ValidationError | null;
  readonly name = 'TypedApiError';

  constructor({
    body,
    message,
    request,
    status,
    statusText,
    url,
  }: ApiError) {
    const ok = status >= 200 && status < 300;
    super(
      request,
      { body, ok, status, statusText, url },
      message,
    );
  }

  private static isGenericErrorBody(
    body: TypedApiError['body'],
    status: TypedApiError['status'],
  ): body is GenericError {
    return Boolean(
      status > 400 &&
      body && 'detail' in body,
    );
  }

  private static isValidationErrorBody(
    body: TypedApiError['body'],
    status: TypedApiError['status'],
  ): body is ValidationError {
    return Boolean(
      status === 400 &&
      body &&
      'errors' in body &&
      'nonFieldErrors' in body,
    );
  }

  get detail() {
    const { body, status } = this;
    return (
      TypedApiError.isGenericErrorBody(body, status)
        ? body.detail
        : ''
    );
  }

  get errors() {
    const { body, status } = this;
    return (
      TypedApiError.isValidationErrorBody(body, status)
        ? body.errors
        : []
    );
  }

  get nonFieldErrors() {
    const { body, status } = this;
    return (
      TypedApiError.isValidationErrorBody(body, status)
        ? body.nonFieldErrors
        : []
    );
  }
}

export const throwTypedApiError = (error: unknown) => {
  if (error instanceof ApiError)
    throw new TypedApiError(error);

  throw error;
};

export const throwIfNotTypedApiError = (error: unknown) => {
  if (!(error instanceof ApiError))
    throw error;
};

export const toTypedApiError = (error: unknown) => {
  if (error instanceof ApiError)
    return new TypedApiError(error);

  return null;
};

export const setReactHookFormErrorByErrors = <T extends FieldValues>(setError: UseFormSetError<T>, errors: ValidationErrorItem[], parentObject?: string) => {
  errors.forEach(({ field, messages }) => setError((parentObject ? `${parentObject}.${field}` : field) as FieldPath<T>, { message: messages.join(', ') }));
};
